import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { CalendarIcon, NewspaperIcon, ArchiveIcon, ChevronDownIcon } from '../components/ui/Icons';

// Accordion Item Component
const AccordionItem = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border border-glass-border rounded-xl mb-3 overflow-hidden transition-all hover:border-accent-gold/50">
      <button
        className="w-full px-5 py-4 flex items-center justify-between text-left bg-glass-bg hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className="flex-1 pr-4">
          <h4 className="text-white font-medium">{item.Title || item.currentAffairsHead}</h4>
          <p className="text-text-muted text-sm mt-1 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            {new Date(item.DateEvent).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <ChevronDownIcon
          className={`w-5 h-5 text-accent-gold transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="px-5 py-4 border-t border-glass-border bg-white/5 animate-slide-down">
          <div
            className="text-text-light leading-relaxed prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: item.currentAffairsContent || item.currentAffairsHead }}
          />
          {item.category && (
            <span className="inline-block mt-3 px-3 py-1 bg-primary-purple/30 text-accent-gold text-xs rounded-full">
              {item.currentAffairsHead}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Archive Item Component
const ArchiveItem = ({ archive, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-4 border rounded-xl transition-all ${
      isActive
        ? 'border-accent-gold bg-accent-gold/10'
        : 'border-glass-border hover:border-accent-gold hover:bg-white/5'
    }`}
  >
    <div className="flex items-center gap-3">
      <CalendarIcon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-accent-gold' : 'text-text-muted'}`} />
      <div>
        {/* Show "January 2026" from the server's monthLabel field */}
        <p className={`font-medium ${isActive ? 'text-accent-gold' : 'text-white'}`}>
          {archive.monthLabel}
        </p>
        {/* <p className="text-text-muted text-sm">{archive.count} articles</p> */}
      </div>
    </div>
  </button>
);

const CurrentAffairsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [affairs, setAffairs] = useState([]);
  const [archives, setArchives] = useState([]);
  const [currentMonthLabel, setCurrentMonthLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(null);

  // URL param is now "month" (YYYY-MM), not "range"
  const selectedMonth = searchParams.get('month');

  // Fetch archive list once on mount
  useEffect(() => {
    api.get('/current-affairs/archives')
      .then(res => { if (res.data.success) setArchives(res.data.data); })
      .catch(err => console.error('Error fetching archives:', err));
  }, []);

  // Fetch articles whenever the selected month changes
  useEffect(() => {
    setLoading(true);
    setOpenAccordion(null);

    const params = selectedMonth ? `?monthKey=${encodeURIComponent(selectedMonth)}` : '';

    api.get(`/current-affairs/by-range${params}`)
      .then(res => {
        if (res.data.success) {
          setAffairs(res.data.data);
          setCurrentMonthLabel(res.data.monthLabel || '');
        }
      })
      .catch(err => console.error('Error fetching current affairs:', err))
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  const toggleAccordion = (id) => setOpenAccordion(openAccordion === id ? null : id);

  const handleArchiveSelect = (monthKey) => setSearchParams({ month: monthKey });

  const handleShowLatest = () => setSearchParams({});

  return (
    <div className="pt-28 pb-16">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-8 mb-10">
        <div className="text-center">
          <h1 className="text-5xl font-extrabold mb-6 gradient-text">Current Affairs</h1>
          <p className="text-xl text-text-muted max-w-3xl mx-auto mb-8">
            Stay updated with the latest news and events essential for your competitive exam preparation
          </p>
          {/* Free Current Affairs Test CTA */}
          <Link
            to="/current-affairs-quiz"
            className="btn-primary inline-flex items-center gap-3 px-8 py-4 text-lg font-bold rounded-full"
          >
            <span>🧪</span>
            <span>Free Current Affairs Test</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <p className="text-xs text-text-muted mt-3">No login required · 5 free tests · Instant results</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

          {/* ── Main Content ── */}
          <div className="lg:col-span-3">
            <div className="glass-card">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <NewspaperIcon className="w-6 h-6 text-accent-gold" />
                  {selectedMonth ? 'Archived Articles' : 'Latest Updates'}
                </h2>
                {currentMonthLabel && (
                  <span className="px-4 py-2 bg-accent-gold/20 text-accent-gold rounded-full text-sm font-medium">
                    {currentMonthLabel}
                  </span>
                )}
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin text-4xl mb-4">⏳</div>
                  <p className="text-text-muted">Loading current affairs...</p>
                </div>
              ) : affairs.length === 0 ? (
                <div className="text-center py-12">
                  <NewspaperIcon className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-text-muted">No current affairs found for this period.</p>
                  <button onClick={handleShowLatest} className="mt-4 text-accent-gold hover:underline">
                    View latest articles
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {affairs.map(item => (
                    <AccordionItem
                      key={item.currentAffairsId}
                      item={item}
                      isOpen={openAccordion === item.currentAffairsId}
                      onToggle={() => toggleAccordion(item.currentAffairsId)}
                    />
                  ))}
                </div>
              )}

              {!loading && affairs.length > 0 && (
                <div className="mt-6 pt-6 border-t border-glass-border text-center text-text-muted">
                  Showing {affairs.length} article{affairs.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div className="lg:col-span-1">
            <div className="glass-card sticky top-28">
              <h3 className="text-xl font-bold text-white flex items-center gap-3 mb-6">
                <ArchiveIcon className="w-6 h-6 text-accent-gold" />
                Archives
              </h3>

              {/* Latest button */}
              <button
                onClick={handleShowLatest}
                className={`w-full text-left p-4 border rounded-xl transition-all mb-4 ${
                  !selectedMonth
                    ? 'border-accent-gold bg-accent-gold/10'
                    : 'border-glass-border hover:border-accent-gold hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <NewspaperIcon className={`w-5 h-5 ${!selectedMonth ? 'text-accent-gold' : 'text-text-muted'}`} />
                  <p className={`font-medium ${!selectedMonth ? 'text-accent-gold' : 'text-white'}`}>
                    Latest Articles
                  </p>
                </div>
              </button>

              <div className="border-t border-glass-border pt-4 mb-4">
                <p className="text-text-muted text-sm mb-3">Previous Months</p>
              </div>

              {archives.length === 0 ? (
                <div className="text-center py-8">
                  <ArchiveIcon className="w-10 h-10 text-text-muted mx-auto mb-3" />
                  <p className="text-text-muted text-sm">No archives available.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                  {archives.map((archive, index) => (
                    <ArchiveItem
                      key={index}
                      archive={archive}
                      isActive={selectedMonth === archive.monthKey}
                      onClick={() => handleArchiveSelect(archive.monthKey)}
                    />
                  ))}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-glass-border">
                <Link
                  to="/"
                  className="text-text-muted hover:text-accent-gold transition-colors text-sm flex items-center gap-2"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentAffairsPage;